import { ElephantLogoIcon } from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import type { Territory } from "$global-providers/config-provider/provider";
import IconButton from "@material-ui/core/IconButton";
import { ChatBubbleOutline, CloseOutlined } from "@material-ui/icons";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { makeDefaultWidgetPositionStyles, type Position } from "./positioning";
import type { EmitAnalyticsEvent } from "./types";

interface Props {
  territory: Territory;
  emitAnalyticsEvent?: EmitAnalyticsEvent;
  positionOverrides?: Position;
}

// A lot of these styles have just been ripped from the Freshdesk widget to try and stay consistent
// We've not added them to the theme because they won't be used anywhere else
const useStyles = makeStyles<Pick<Props, "positionOverrides">>(() => ({
  wrapper: (props) => ({
    position: "fixed",
    bottom: 20,
    right: 20,
    zIndex: 100,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    ...(props.positionOverrides &&
      makeDefaultWidgetPositionStyles(props.positionOverrides)),
  }),
  openIcon: {
    background: "#483c8b !important",
    boxShadow: "0 5px 4px 0 rgb(0 0 0 / 26%)",
    height: "60px",
    width: "60px",
  },
  closeIcon: {
    marginBottom: "5px",
    background: " #707c92 !important",
    borderRadius: "1rem",
    height: "1.75rem",
    width: "2.5rem",
    opacity: "0.7",
    "&:hover": {
      opacity: "1",
    },
  },
  widget: {
    height: "300px",
    width: "300px",
    background: "#f4f5f7",
    boxShadow: "0 -1px 20px rgb(0 0 0 / 16%)",
    borderRadius: "5px",
    overflow: "hidden",
  },
  header: {
    width: "100%",
    height: "80px",
    background: "#483c8b",
    display: "flex",
    alignItems: "center",
    padding: "20px",
  },
  logo: {
    width: "163px",
    height: "35px",
  },
  body: {
    padding: "15px",
  },
  bodyCard: {
    background: "white",
    borderRadius: "5px",
    width: "100%",
    boxShadow: "0 2px 4px 0 rgb(0 0 0 / 12%)",
    padding: "8px",
  },
}));

function getSupportDetails(territory: Territory) {
  if (territory === "kenya") {
    return "+254748369986";
  }
  return null;
}

const DefaultHelpWidget: FC<Props> = ({
  territory,
  emitAnalyticsEvent,
  positionOverrides,
}) => {
  const classes = useStyles({ positionOverrides });
  const [isOpen, setIsOpen] = useState(false);
  const { formatMessage } = useIntl();

  // Allow this widget to open in place of the freshdesk one if anywhere in the application attempts to
  // use the global window methods to open the widget
  useEffect(() => {
    const previousFcWidget = window.fcWidget;
    const previousOpen = window.fcWidget?.open;

    window.fcWidget = {
      ...previousFcWidget,
      open: (...args: any[]) => {
        setIsOpen(true);
        if (previousOpen) {
          previousOpen(...args);
        }
      },
    };

    return () => {
      window.fcWidget = previousFcWidget;
      if (window.fcWidget) {
        window.fcWidget.open = previousOpen;
      }
    };
  }, []);

  const supportDetails = getSupportDetails(territory);

  return (
    <div className={classes.wrapper}>
      {isOpen ? (
        <>
          <IconButton
            aria-label={formatMessage({
              id: "defaultHelpWidget.closeLabel",
              defaultMessage: "close support window",
            })}
            className={classes.closeIcon}
            onClick={() => setIsOpen(false)}
          >
            <CloseOutlined style={{ color: "white" }} />
          </IconButton>

          <div className={classes.widget}>
            <div className={classes.header}>
              <ElephantLogoIcon className={classes.logo} fill="white" />
            </div>
            <div className={classes.body}>
              <div className={classes.bodyCard}>
                <p>
                  <FormattedMessage
                    id="defaultHelpWidget.body1"
                    defaultMessage="You are unable to use our support chat at the moment. This might be because you are offline or because you have no data left on your device."
                  />
                </p>
                {supportDetails ? (
                  <p>
                    <FormattedMessage
                      id="defaultHelpWidget.body2"
                      defaultMessage="Please connect to the internet and try again, or contact us at <b>{supportMethod}</b>"
                      values={{
                        // @ts-ignore
                        b: (...chunks: any[]) => <b>{chunks}</b>,
                        supportMethod: getSupportDetails(territory),
                      }}
                    />
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : (
        <IconButton
          aria-label={formatMessage({
            id: "defaultHelpWidget.openLabel",
            defaultMessage: "open support window",
          })}
          className={classes.openIcon}
          onClick={() => {
            emitAnalyticsEvent?.({
              category: "freshdesk",
              name: "freshdesk.fallback_widget.open",
            });
            setIsOpen(true);
          }}
        >
          <ChatBubbleOutline style={{ color: "white" }} />
        </IconButton>
      )}
    </div>
  );
};

export default DefaultHelpWidget;
